import React, { useState, useEffect } from 'react';
import { useGoals } from '../contexts/GoalContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import GoalForm from './GoalForm';

const GoalList = () => {
  const { goals, loading, fetchGoals, deleteGoal, updateGoal } = useGoals();
  const { user, hasAnyRole } = useAuth();
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    level: '',
    assigned_to_me: false
  });
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [expandedGoal, setExpandedGoal] = useState(null);

  useEffect(() => {
    fetchGoals(filters);
  }, [filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleDeleteGoal = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      const result = await deleteGoal(goalId);
      if (!result.success) {
        alert(`Failed to delete goal: ${result.error}`);
      }
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingGoal(null);
    fetchGoals(filters);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingGoal(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      archived: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const canEditGoal = (goal) => {
    return (
      goal.created_by === user?.id ||
      goal.assigned_to.includes(user?.id) ||
      hasAnyRole(['manager', 'hr', 'admin'])
    );
  };

  const canDeleteGoal = (goal) => {
    return (
      goal.created_by === user?.id ||
      hasAnyRole(['manager', 'hr', 'admin'])
    );
  };

  if (showForm) {
    return (
      <GoalForm
        goal={editingGoal}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Goals</h1>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Create Goal
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                id="category"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Categories</option>
                <option value="personal">Personal</option>
                <option value="professional">Professional</option>
                <option value="skill_development">Skill Development</option>
                <option value="performance">Performance</option>
                <option value="innovation">Innovation</option>
                <option value="leadership">Leadership</option>
                <option value="collaboration">Collaboration</option>
                <option value="customer_focus">Customer Focus</option>
              </select>
            </div>

            {/* Level Filter */}
            <div>
              <label htmlFor="level" className="block text-sm font-medium text-gray-700">
                Level
              </label>
              <select
                id="level"
                value={filters.level}
                onChange={(e) => handleFilterChange('level', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Levels</option>
                <option value="individual">Individual</option>
                <option value="team">Team</option>
                <option value="department">Department</option>
                <option value="organization">Organization</option>
              </select>
            </div>

            {/* My Goals Filter */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.assigned_to_me}
                  onChange={(e) => handleFilterChange('assigned_to_me', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">My Goals Only</span>
              </label>
            </div>
          </div>
        </div>

        {/* Goals List */}
        {loading ? (
          <LoadingSpinner size="lg" text="Loading goals..." />
        ) : goals.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No goals found</h3>
            <p className="text-gray-500">Create your first goal to get started!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {goals.map(goal => (
              <div key={goal.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">{goal.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                          {goal.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(goal.priority)}`}>
                          {goal.priority.toUpperCase()}
                        </span>
                      </div>
                      
                      {goal.description && (
                        <p className="text-gray-600 mt-2">{goal.description}</p>
                      )}
                      
                      <div className="mt-3 flex items-center space-x-6 text-sm text-gray-500">
                        <span>Category: {goal.category.replace('_', ' ')}</span>
                        <span>Level: {goal.level}</span>
                        <span>Due: {formatDate(goal.due_date)}</span>
                        {goal.target_value && (
                          <span>Target: {goal.target_value} {goal.unit}</span>
                        )}
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Progress</span>
                          <span>{Math.round(goal.progress_percentage)}%</span>
                        </div>
                        <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${goal.progress_percentage}%` }}
                          ></div>
                        </div>
                        {goal.current_value !== undefined && goal.target_value && (
                          <div className="mt-1 text-xs text-gray-500">
                            {goal.current_value} / {goal.target_value} {goal.unit}
                          </div>
                        )}
                      </div>
                      
                      {/* Tags */}
                      {goal.tags && goal.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {goal.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setExpandedGoal(expandedGoal === goal.id ? null : goal.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {expandedGoal === goal.id ? 'Less' : 'More'}
                      </button>
                      {canEditGoal(goal) && (
                        <button
                          onClick={() => handleEditGoal(goal)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {canDeleteGoal(goal) && (
                        <button
                          onClick={() => handleDeleteGoal(goal.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedGoal === goal.id && (
                    <div className="mt-6 border-t pt-6">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <h4 className="font-medium text-gray-900">Details</h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p>Created: {formatDate(goal.created_at)}</p>
                            <p>Started: {formatDate(goal.start_date)}</p>
                            <p>Due: {formatDate(goal.due_date)}</p>
                            <p>Last Updated: {formatDate(goal.updated_at)}</p>
                          </div>
                        </div>
                        {goal.sub_goals && goal.sub_goals.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-900">Sub-goals</h4>
                            <div className="mt-2 text-sm text-gray-600">
                              {goal.sub_goals.length} sub-goal{goal.sub_goals.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalList;